import type { DashboardCompany } from "../lib/api";
import { BrandGroup } from "./BrandGroup";
import { summarize, formatNumber, formatDelta, deltaClass } from "../lib/format";

export function CompanyView({ company }: { company: DashboardCompany }) {
  const allSites = company.brands.flatMap((b) => b.sites);

  const aggregated = aggregateAcrossSites(allSites);
  const visits = summarize(aggregated, "visits");
  const uniques = summarize(aggregated, "uniqueVisitors");
  const pageviews = summarize(aggregated, "pageviews");

  return (
    <div>
      {/* Company-level summary */}
      <div className="mb-12 pb-10 border-b border-border">
        <div className="grid grid-cols-3 gap-8">
          <Stat label="Visits"     today={visits.today}    dod={visits.dod}    wow={visits.wow}    />
          <Stat label="Unique"     today={uniques.today}   dod={uniques.dod}   wow={uniques.wow}   />
          <Stat label="Pageviews"  today={pageviews.today} dod={pageviews.dod} wow={pageviews.wow} />
        </div>
      </div>

      {/* Brand groups */}
      <div>
        {company.brands.map((brand) => (
          <BrandGroup key={brand.id} brand={brand} />
        ))}

        {company.brands.length === 0 && (
          <div className="text-center py-20 text-faint font-display italic">
            まだサイトが登録されていません
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  today,
  dod,
  wow,
}: {
  label: string;
  today: number;
  dod: number | null;
  wow: number | null;
}) {
  return (
    <div>
      <div className="text-[11px] tracking-editorial text-muted uppercase mb-3 font-medium">
        {label}
      </div>
      <div className="font-display tnum text-5xl text-ink leading-none mb-3 tracking-tightest font-light">
        {formatNumber(today)}
      </div>
      <div className="flex items-center gap-2.5 text-xs tnum">
        <span className={deltaClass(dod)}>{formatDelta(dod)}</span>
        <span className="text-faint">D/D</span>
        <span className="text-whisper">·</span>
        <span className={deltaClass(wow)}>{formatDelta(wow)}</span>
        <span className="text-faint">W/W</span>
      </div>
    </div>
  );
}

function aggregateAcrossSites(
  sites: Array<{
    snapshots: Array<{
      date: string;
      visits: number;
      uniqueVisitors: number;
      pageviews: number;
    }>;
  }>,
) {
  const byDate = new Map<
    string,
    { date: string; visits: number; uniqueVisitors: number; pageviews: number }
  >();
  for (const site of sites) {
    for (const s of site.snapshots) {
      const existing = byDate.get(s.date);
      if (existing) {
        existing.visits += s.visits;
        existing.uniqueVisitors += s.uniqueVisitors;
        existing.pageviews += s.pageviews;
      } else {
        byDate.set(s.date, { ...s });
      }
    }
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}
